import { m } from "framer-motion";

const FeatureSection = ({
  title,
  description,
  image,
  bulletPoints,
  reversed,
  bgClass = "bg-white",
  topDecoration,
  padding = "py-12 md:py-20",
}) => {
  return (
    <section className={`${padding} ${bgClass} overflow-hidden relative`}>
      {topDecoration && (
        <div className="absolute top-0 left-0 w-full z-0 pointer-events-none">
          {topDecoration}
        </div>
      )}
      <div
        className={`px-6 max-w-[1614px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12 relative z-10 ${reversed ? "md:flex-row-reverse" : ""
          }`}
      >
        {/* Text Content */}
        <m.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0, x: reversed ? 50 : -50 },
            visible: {
              opacity: 1,
              x: 0,
              transition: {
                duration: 0.6,
                staggerChildren: 0.1,
              },
            },
          }}
          className="w-full md:w-1/2 space-y-4 md:space-y-6 max-w-[570px]"
        >
          <m.h2
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="text-[36px] md:text-[42px] lg:text-[60px] leading-tight font-bold text-[#09042C]"
          >
            {title}
          </m.h2>

          <m.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="text-[#575757] text-[18px] md:text-[20px] lg:text-[22px] font-normal leading-[150%]"
          >
            {description}
          </m.p>

          {bulletPoints && bulletPoints.length > 0 && (
            <m.ul
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1, delayChildren: 0.2 },
                },
              }}
              className="space-y-3 pt-4"
            >
              {bulletPoints.map((point, index) => (
                <m.li
                  key={index}
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    visible: { opacity: 1, x: 0 },
                  }}
                  className="flex items-start gap-3 text-slate-600 font-medium"
                >
                  <div className="w-2 h-2 rounded-full bg-[#09042C] mt-2.5 shrink-0"></div>
                  <span className="text-[#575757] text-[18px] md:text-[20px] lg:text-[22px] font-normal leading-[110%]">
                    {point}
                  </span>
                </m.li>
              ))}
            </m.ul>
          )}
        </m.div>

        {/* Image/Visual Content */}
        <m.div
          initial={{ opacity: 0, scale: 0.9, x: reversed ? -50 : 50 }}
          whileInView={{ opacity: 1, scale: 1, x: 0 }}
          transition={{
            duration: 0.8,
            delay: 0.2,
            type: "spring",
            bounce: 0.4,
          }}
          viewport={{ once: true, margin: "-100px" }}
          className="w-full md:w-1/2"
        >
          <div className="relative">
            {/* Background Blob/Shape */}
            <m.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.7, 0.5] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-orange-50/50 rounded-full blur-3xl -z-10"
            ></m.div>

            {/* The main image/component */}
            <m.div
              animate={{ y: [0, -15, 0] }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="transform hover:scale-[1.02] transition-transform duration-500"
            >
              {image ? (
                image
              ) : (
                <div className="aspect-[4/3] bg-slate-50 rounded-xl flex items-center justify-center text-slate-300">
                  Image Placeholder
                </div>
              )}
            </m.div>
          </div>
        </m.div>
      </div>
    </section>
  );
};

export default FeatureSection;
